import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronRight, RefreshCw, CheckCircle, AlertCircle, Database,
  Calendar, Settings, Link2,
} from 'lucide-react'
import { api } from '@/lib/api/client'
import { BrandGlyph } from './IntegrationsPage'
import type { ClementineStatus, ClementineSyncLogEntry } from '@/types/api'

const GLYPH = {
  monogram: 'CLM',
  gradient: 'linear-gradient(135deg, oklch(0.72 0.052 198) 0%, oklch(0.56 0.062 202) 100%)',
}

type Tab = 'log' | 'config'

export default function ClementineIntegrationPage() {
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<Tab>('log')

  const { data: status, isLoading } = useQuery({
    queryKey: ['clementine', 'status'],
    queryFn:  () => api.get<ClementineStatus>('/clementine-budget/status'),
  })
  const { data: syncLog = [] } = useQuery({
    queryKey: ['clementine', 'sync-log'],
    queryFn:  () => api.get<ClementineSyncLogEntry[]>('/clementine-budget/sync/status'),
    enabled:  status?.configured === true,
  })

  const syncMutation = useMutation({
    mutationFn: () => api.post<Record<string, number>>('/clementine-budget/sync', {}),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['clementine'] }),
  })
  const disconnectMutation = useMutation({
    mutationFn: () => api.delete<unknown>('/clementine-budget/config'),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ['clementine'] }),
  })
  const handleDisconnect = () => {
    if (!confirm('Disconnect Clementine? The sync will stop and credentials will be deleted.')) return
    disconnectMutation.mutate()
  }

  if (isLoading) {
    return <div className="page"><Breadcrumb /><p className="muted" style={{ paddingTop: 32, textAlign: 'center' }}>Loading…</p></div>
  }

  // ── Not configured ─────────────────────────────────────────────────────────
  if (!status?.configured) {
    return (
      <div className="page">
        <Breadcrumb />
        <div className="card" style={{ maxWidth: 560, padding: 32 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 24 }}>
            <BrandGlyph {...GLYPH} size={48} />
            <div>
              <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 600 }}>Clementine</h2>
              <p className="muted" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6 }}>
                Budget and forecast data from Clementine's fortnoxdata database.
                Enter the connection details below to enable the sync.
              </p>
            </div>
          </div>
          <ManualConfigForm onSaved={() => queryClient.invalidateQueries({ queryKey: ['clementine'] })} />
        </div>
      </div>
    )
  }

  // ── Configured ─────────────────────────────────────────────────────────────
  const isHealthy   = status.lastSyncStatus !== 'error'
  const lastSyncAt  = status.lastSyncAt ? new Date(status.lastSyncAt) : null
  const lastSyncStr = lastSyncAt ? lastSyncAt.toLocaleString('sv-SE', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : null

  return (
    <div className="page">
      <Breadcrumb />

      {/* Hero */}
      <div className="card" style={{
        padding: 24, marginBottom: 20,
        background: 'linear-gradient(135deg, var(--ns-surface) 0%, var(--ns-surface-2) 100%)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, background: 'radial-gradient(circle, oklch(0.72 0.052 198 / 0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="row" style={{ gap: 18, alignItems: 'flex-start', position: 'relative' }}>
          <BrandGlyph {...GLYPH} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 4 }}>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-0.015em' }}>Clementine</h1>
              <span className={`badge ${isHealthy ? 'badge-ok' : 'badge-warn'}`}>
                <span className={`dot ${isHealthy ? 'dot-ok' : 'dot-warn'}`} />
                {isHealthy ? 'Active' : 'Degraded'}
              </span>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>
              Budget and forecast data from fortnoxdata. Run sync manually or trigger from a scheduler.
            </p>
            {lastSyncStr && (
              <div className="row" style={{ gap: 5, marginTop: 10, fontSize: 11.5, color: 'var(--ns-text-muted)' }}>
                <Calendar size={12} />
                <span>Last synced {lastSyncStr}</span>
              </div>
            )}
          </div>
          <div className="row" style={{ gap: 6, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-sm" style={{ gap: 5, color: 'var(--ns-err)' }}
              onClick={handleDisconnect} disabled={disconnectMutation.isPending}>
              <Link2 size={13} />Disconnect
            </button>
            <button
              className="btn btn-brand btn-sm" style={{ gap: 5 }}
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
            >
              <RefreshCw size={13} style={{ animation: syncMutation.isPending ? 'spin 0.8s linear infinite' : undefined }} />
              {syncMutation.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          </div>
        </div>
        {syncMutation.data && !syncMutation.isPending && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--ns-border)', display: 'flex', gap: 20, fontSize: 12.5, flexWrap: 'wrap' }}>
            {Object.entries(syncMutation.data).map(([entity, count]) => (
              <span key={entity}><strong className="tabular">{String(count)}</strong> <span className="muted">{entity}</span></span>
            ))}
          </div>
        )}
        {syncMutation.isError && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--ns-border)', color: 'var(--ns-err)', fontSize: 12 }}>
            Sync failed: {(syncMutation.error as Error).message}
          </div>
        )}
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 22 }}>
        <DetailStat label="Forecast versions" value={(status.forecastVersionCount ?? 0).toLocaleString('sv-SE')} sub="ForecastVersion" />
        <DetailStat label="Forecasts"         value={(status.forecastCount         ?? 0).toLocaleString('sv-SE')} sub="Forecast rows" />
        <DetailStat label="Cost centers"      value={(status.costCenterCount       ?? 0).toLocaleString('sv-SE')} sub="CostCenter" />
        <DetailStat label="Result sheets"     value={(status.resultSheetCount      ?? 0).toLocaleString('sv-SE')} sub="ResultSheet" />
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: 0 }}>
        {([['log', 'Sync log'], ['config', 'Configuration']] as [Tab, string][]).map(([id, label]) => (
          <button key={id} className={`tab${tab === id ? ' active' : ''}`} onClick={() => setTab(id)}>
            {id === 'log' ? <><Database size={13} />{label}</> : <><Settings size={13} />{label}</>}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 18 }}>
        {tab === 'log'    && <SyncLogTab log={syncLog} />}
        {tab === 'config' && <ConfigTab onSaved={() => queryClient.invalidateQueries({ queryKey: ['clementine'] })} />}
      </div>
    </div>
  )
}

// ── Sync log tab ───────────────────────────────────────────────────────────

function SyncLogTab({ log }: { log: ClementineSyncLogEntry[] }) {
  const byRun = groupByRun(log)

  return (
    <div className="card" style={{ padding: 0 }}>
      {byRun.length === 0 && (
        <div className="muted" style={{ textAlign: 'center', padding: 32 }}>
          No sync history yet — click Sync now to run the first sync.
        </div>
      )}
      {byRun.map((run, i) => (
        <div key={i} style={{ borderTop: i > 0 ? '1px solid var(--ns-border)' : undefined }}>
          <div className="row" style={{ padding: '10px 14px', background: 'var(--ns-surface-2)', gap: 10 }}>
            <span className="mono tabular muted" style={{ fontSize: 11 }}>{run.syncedAt}</span>
            <span className={`badge ${run.hasError ? 'badge-warn' : 'badge-ok'}`} style={{ fontSize: 10.5 }}>
              <span className={`dot ${run.hasError ? 'dot-warn' : 'dot-ok'}`} />
              {run.hasError ? 'Error' : 'Success'}
            </span>
            <span className="muted" style={{ fontSize: 11.5, flex: 1 }}>
              {run.entries.reduce((s, e) => s + e.recordsUpserted, 0).toLocaleString()} records total
            </span>
          </div>
          {run.entries.map((e, j) => (
            <div key={j} className="row" style={{ padding: '8px 14px 8px 28px', borderTop: '1px solid var(--ns-border)', gap: 12 }}>
              {e.status === 'success'
                ? <CheckCircle size={13} style={{ color: 'var(--ns-ok)', flexShrink: 0 }} />
                : <AlertCircle size={13} style={{ color: 'var(--ns-err)', flexShrink: 0 }} />
              }
              <span style={{ fontSize: 12.5, flex: 1 }}>
                {e.errorMessage
                  ? <span style={{ color: 'var(--ns-err)' }}>{e.errorMessage}</span>
                  : <><strong className="tabular">{e.recordsUpserted.toLocaleString()}</strong> <span className="muted">rows</span></>
                }
              </span>
              <span style={{ fontSize: 11.5, color: 'var(--ns-text-muted)', minWidth: 120, textAlign: 'right' }}>{e.entity}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

function groupByRun(log: ClementineSyncLogEntry[]) {
  const runs: { syncedAt: string; hasError: boolean; entries: ClementineSyncLogEntry[] }[] = []
  let current: typeof runs[0] | null = null
  for (const entry of log) {
    const ts = entry.syncedAt
      ? new Date(entry.syncedAt).toLocaleString('sv-SE', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      : '—'
    if (!current || current.syncedAt !== ts) {
      current = { syncedAt: ts, hasError: false, entries: [] }
      runs.push(current)
    }
    if (entry.status !== 'success') current.hasError = true
    current.entries.push(entry)
  }
  return runs
}

// ── Config tab ─────────────────────────────────────────────────────────────

function ConfigTab({ onSaved }: { onSaved: () => void }) {
  const { data: cfg } = useQuery({
    queryKey: ['clementine', 'config'],
    queryFn:  () => api.get<{ configured: boolean; server?: string; database?: string; username?: string }>('/clementine-budget/config'),
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14 }}>Current connection</div>
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '8px 16px', fontSize: 13, alignItems: 'center' }}>
          <span className="muted">Server</span>
          <span className="mono" style={{ fontSize: 12 }}>{cfg?.server ?? '—'}</span>
          <span className="muted">Database</span>
          <span className="mono" style={{ fontSize: 12 }}>{cfg?.database ?? '—'}</span>
          <span className="muted">Username</span>
          <span className="mono" style={{ fontSize: 12 }}>{cfg?.username ?? '—'}</span>
        </div>
      </div>

      <ManualConfigForm onSaved={onSaved} />
    </div>
  )
}

// ── Manual config form ─────────────────────────────────────────────────────

function ManualConfigForm({ onSaved }: { onSaved: () => void }) {
  const [server,   setServer]   = useState('')
  const [database, setDatabase] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const saveMutation = useMutation({
    mutationFn: () => api.post('/clementine-budget/config', { server, database, username, password }),
    onSuccess: onSaved,
  })

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14 }}>Manual configuration</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {([
          ['Server',   server,   setServer,   'forge-dev-sql.database.windows.net'],
          ['Database', database, setDatabase, 'fortnoxdata'],
          ['Username', username, setUsername, 'sa_forge'],
          ['Password', password, setPassword, '••••••••', 'password'],
        ] as [string, string, (v: string) => void, string, string?][]).map(([label, val, set, ph, type]) => (
          <div key={label} style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 10, alignItems: 'center' }}>
            <label className="muted" style={{ fontSize: 13 }}>{label}</label>
            <input
              className="input" type={type ?? 'text'} value={val}
              onChange={e => set(e.target.value)} placeholder={ph}
              style={{ height: 32, fontSize: 13, padding: '0 10px', fontFamily: type ? undefined : 'JetBrains Mono, monospace' }}
            />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button
            className="btn btn-brand btn-sm"
            onClick={() => saveMutation.mutate()}
            disabled={!server || !database || !username || !password || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
          {saveMutation.isSuccess && <span className="muted" style={{ fontSize: 12, alignSelf: 'center' }}>Saved</span>}
          {saveMutation.isError   && <span style={{ fontSize: 12, color: 'var(--ns-err)', alignSelf: 'center' }}>{(saveMutation.error as Error).message}</span>}
        </div>
      </div>
    </div>
  )
}

// ── Shared helpers ─────────────────────────────────────────────────────────

function Breadcrumb() {
  return (
    <div className="row" style={{ gap: 4, marginBottom: 14, color: 'var(--ns-text-muted)', fontSize: 12 }}>
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Settings</Link>
      <ChevronRight size={11} />
      <Link to="/settings/integrations" style={{ color: 'inherit' }}>Integrations</Link>
      <ChevronRight size={11} />
      <span style={{ color: 'var(--ns-text)' }}>Clementine</span>
    </div>
  )
}

function DetailStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="muted" style={{ fontSize: 11, fontWeight: 500, marginBottom: 6, letterSpacing: '0.01em' }}>{label}</div>
      <div className="mono tabular" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>{sub}</div>}
    </div>
  )
}
